import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    simulateGatewayPassFormSchema,
    SimulateGatewayPassFormSchemaValues,
    useSimulateGatewayPass,
} from 'src/features/actions/api/useSimulateGatewayPass';
import SimulateLPRDetectionForm from 'src/features/actions/components/form/SimulateLPRDetectionForm';
import { actionsMessages } from 'src/features/actions/utils/messages';
import { toast } from 'src/hooks/UseToast';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';

type CreateLPRDetectionFormModalProps = {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
};
const SimulateLPRDetectionFormModal = ({ isModalOpen, setIsModalOpen }: CreateLPRDetectionFormModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const form = useForm<SimulateGatewayPassFormSchemaValues>({
        resolver: zodResolver(simulateGatewayPassFormSchema),
        defaultValues: {},
    });

    const onError = useOnError();

    const { mutate, isPending } = useSimulateGatewayPass({
        onSuccess: () => {
            setIsModalOpen(false);
            toast({
                title: formatMessage(actionsMessages.createdLPRdetection),
                description: formatMessage(actionsMessages.createdLPRdetectionDescription),
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const onSubmit: SubmitHandler<SimulateGatewayPassFormSchemaValues> = (values) => {
        const formData = new FormData();

        formData.append('gateway', values.gateway);
        formData.append('identifier_type', values.identifier_type);
        formData.append('identifier_value', values.identifier_value);

        if (values.date || values.time) {
            const date = values.date ? new Date(values.date) : new Date();

            if (values.time) {
                const [hours, minutes, seconds] = values.time.split(':').map(Number);
                date.setHours(hours ?? 0, minutes ?? 0, seconds ?? 0);
            } else if (values.date) {
                const now = new Date();
                date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
            }

            formData.append('time', date.toISOString());
        }

        if (values.files) {
            values.files.forEach((file) => {
                formData.append('files', file);
            });
        }

        mutate({ formData, parkingId });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
        if (isOpen) {
            form.reset();
        }
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.CAMERA} />
                        <div>
                            <DialogTitle>{formatMessage(actionsMessages.lprDetection)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(actionsMessages.lprDetectionDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <SimulateLPRDetectionForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button variant="default" type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(actionsMessages.createLprDetection)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SimulateLPRDetectionFormModal;
